import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    description: '',
    projectId: '',
    assigneeId: '',
    statusId: '',
    deadline: ''
  });

  const clientStatuses = [
    { value: 'закупает регулярный', label: 'закупает регулярный', color: '#4CAF50' },
    { value: 'закупает редко', label: 'закупает редко', color: '#4CAF50' },
    { value: 'порулонщик', label: 'порулонщик', color: '#FF9800' },
    { value: 'отработан, у конкурентов', label: 'отработан, у конкурентов', color: '#FF9800' },
    { value: 'общаемся, но не покупает', label: 'общаемся, но не покупает', color: '#FF9800' },
    { value: 'не контактировали', label: 'не контактировали', color: '#F44336' },
    { value: 'отработан, не с рынка', label: 'отработан, не с рынка', color: '#F44336' }
  ];

  const getClientStatusMeta = (statusValue) => {
    const found = clientStatuses.find(s => s.value === statusValue);
    if (found) return found;
    if (!statusValue) return { label: '-', color: '#9e9e9e' };
    return { label: String(statusValue), color: '#9e9e9e' };
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientRes, tasksRes, projectsRes, statusesRes, usersRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get('/tasks'),
        api.get('/projects'),
        api.get('/statuses'),
        api.get('/auth/users')
      ]);

      setClient(clientRes.data);

      // Фильтруем задачи по clientId (и делаем fallback через project.client для старых записей)
      const clientTasks = tasksRes.data.filter(t =>
        t.clientId === id || t.project?.client?.id === id
      );

      // Держим полный список проектов, иначе кнопка/селекты могут быть пустыми,
      // если проекты не были привязаны к клиенту.
      setTasks(clientTasks);
      setProjects(projectsRes.data);
      setStatuses(statusesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateTask = () => {
    setError('');

    if (!projects.length) {
      setError('Сначала создайте проект');
      return;
    }

    const defaultProjectId =
      projects.find(p => p.clientId === id)?.id ||
      projects[0]?.id ||
      '';

    setTaskForm({
      description: '',
      projectId: defaultProjectId,
      assigneeId: user?.id || users[0]?.id || '',
      statusId: statuses[0]?.id || '',
      deadline: ''
    });
    setTaskDialogOpen(true);
  };

  const handleCloseCreateTask = () => {
    setTaskDialogOpen(false);
    setEditingTask(null);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      description: task.description,
      projectId: task.projectId,
      assigneeId: task.assigneeId,
      statusId: task.statusId,
      deadline: task.deadline ? task.deadline.split('T')[0] : ''
    });
    setTaskDialogOpen(true);
  };

  const getApiErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || '';
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map(e => e?.msg).filter(Boolean).join(', ');
    }
    return err?.message || '';
  };

  const handleCreateTask = async () => {
    try {
      setError('');
      const payload = {
        description: taskForm.description,
        clientId: id,
        projectId: taskForm.projectId || null,
        assigneeId: taskForm.assigneeId || null,
        statusId: taskForm.statusId || null,
        deadline: taskForm.deadline === '' ? null : taskForm.deadline
      };

      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, payload);
      } else {
        await api.post('/tasks', payload);
      }
      await loadData();
      handleCloseCreateTask();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setError(msg ? `Ошибка сохранения задачи: ${msg}` : 'Ошибка сохранения задачи');
    }
  };

  const getFilteredTasks = () => {
    return tasks
      .filter(t => projectFilter === 'all' || t.projectId === projectFilter)
      .filter(t => statusFilter === 'all' || t.statusId === statusFilter)
      .sort((a, b) => {
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline) - new Date(b.deadline);
      });
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredTasks = getFilteredTasks();

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate('/clients')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" flex={1}>
          {client?.companyName}
        </Typography>
        <Button
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
          size="small"
          variant="outlined"
        >
          {!isMobile && 'Главная'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Информация о клиенте */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6">Информация</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateTask}
              disabled={!projects.length}
            >
              Добавить задачу
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Статус:{' '}
            <Chip
              label={getClientStatusMeta(client?.status).label}
              size="small"
              sx={{
                bgcolor: getClientStatusMeta(client?.status).color,
                color: 'white'
              }}
            />
          </Typography>
          {client?.generalInfo && (
            <Typography variant="body2" color="text.secondary">
              {client.generalInfo}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Create task from client */}
      <Dialog
        open={taskDialogOpen}
        onClose={handleCloseCreateTask}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{editingTask ? 'Редактировать задачу' : 'Добавить задачу'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Описание"
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              required
              fullWidth
              size="small"
              multiline
              rows={8}
            />
            <TextField
              select
              label="Проект"
              value={taskForm.projectId}
              onChange={(e) => setTaskForm({ ...taskForm, projectId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Исполнитель"
              value={taskForm.assigneeId}
              onChange={(e) => setTaskForm({ ...taskForm, assigneeId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Статус"
              value={taskForm.statusId}
              onChange={(e) => setTaskForm({ ...taskForm, statusId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дедлайн"
              type="date"
              value={taskForm.deadline}
              onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateTask}>Отмена</Button>
          <Button
            onClick={handleCreateTask}
            variant="contained"
            disabled={!taskForm.description || !taskForm.projectId || !taskForm.assigneeId || !taskForm.statusId}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Фильтры */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
            <TextField
              select
              label="Проект"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? '100%' : 200 }}
              size="small"
            >
              <MenuItem value="all">Все проекты</MenuItem>
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Статус задачи"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? '100%' : 200 }}
              size="small"
            >
              <MenuItem value="all">Все статусы</MenuItem>
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Задачи */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Задачи ({filteredTasks.length})
          </Typography>

          {isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
              {filteredTasks.map((task) => (
                <Card
                  key={task.id}
                  onClick={() => handleEditTask(task)}
                  sx={{
                    bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'white',
                    border: isOverdue(task.deadline) ? '1px solid #ef5350' : 'none',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 }
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      {task.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {task.project?.name} • {task.assignee?.fullName}
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={task.status?.name}
                        size="small"
                        sx={{
                          bgcolor: task.status?.color,
                          color: 'white',
                          height: 20,
                          fontSize: '0.7rem'
                        }}
                      />
                      {task.deadline && (
                        <Chip
                          label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                          size="small"
                          color={isOverdue(task.deadline) ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {filteredTasks.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  Нет задач
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Описание</TableCell>
                    <TableCell>Проект</TableCell>
                    <TableCell>Исполнитель</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дедлайн</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow
                      key={task.id}
                      onClick={() => handleEditTask(task)}
                      sx={{
                        bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'inherit',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isOverdue(task.deadline) ? '#ffcdd2' : 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{task.description}</TableCell>
                      <TableCell>{task.project?.name}</TableCell>
                      <TableCell>{task.assignee?.fullName}</TableCell>
                      <TableCell>
                        <Chip
                          label={task.status?.name}
                          size="small"
                          sx={{
                            bgcolor: task.status?.color,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {task.deadline ? (
                          <Chip
                            label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                            size="small"
                            color={isOverdue(task.deadline) ? 'error' : 'default'}
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">Нет задач</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
